import targetingApi from 'domain/targeting/api'
import { locationResultKey } from 'domain/targeting/mappers'
import i18n from 'i18n'
import { immutableSplice } from 'utils/helpers'
import { addLocation, locationContains } from './locationManager'

export const MIN_RADIUS_MILES = 10
export const MIN_RADIUS_KILOMETERS = 17
export const MAX_RADIUS_MILES = 50
export const MAX_RADIUS_KILOMETERS = 80

export const MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS = 10
export const MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS = 15
export const MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS_US_CA = 25
export const MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS_US_CA = 40

export const actions = Object.freeze({
  addLocation: 'addLocation',
  removeLocation: 'removeLocation',
  clearAll: 'clearAll',
  setLocationRadius: 'setLocationRadius',
  setQualifier: 'setQualifier',
  togglePlatform: 'togglePlatform',
  reset: 'reset',

  addLocationAsync: 'addLocationAsync',
  fetchSuggestedRadiusAsync: 'fetchgetSuggestedRadiusAsync',
  fetchSuggestedRadiusSuccess: 'fetchSuggestedRadiusSuccess',
  fetchSuggestedRadiusError: 'fetchSuggestedRadiusError',
  fetchSuggestedRadiusLoading: 'fetchSuggestedRadiusLoading',
})

export const _initialState = {
  locations: [],
}

const milesToKilometers = (miles) => {
  const km = miles * 1.60934

  return Math.max(MIN_RADIUS_KILOMETERS, Math.round(km))
}

const kilometersToMiles = (kilometers) => {
  const mi = kilometers / 1.60934

  return Math.max(MIN_RADIUS_MILES, Math.round(mi))
}

// `location.location.key` (fb) / `.geo_target_constant_id` (google) are the
// real platform-native ids, kept verbatim for publish — NOT usable to merge
// a fb entry and a google entry for the same real-world place, since
// they're different id spaces. `data.key` is instead always the
// platform-agnostic locationResultKey (canonical_name/name/type/
// country_code), and `data.platform_results.<platform>` is reconstructed
// from the saved wrapper so a reloaded location's native id survives a
// later re-save via splitLocationsByPlatform below — same shape live search
// results already carry (domain/targeting/mappers.js).
const dbLocationToLocation = (location, qualifier, platform) => {
  const unit = getPreferredRangeUnit()
  const raw = location.location
  const nativeResult =
    platform === 'google'
      ? {
        geo_target_constant_id: raw.geo_target_constant_id,
        resource_name: raw.resource_name,
        target_type: raw.target_type,
        country_code: raw.country_code,
      }
      : { key: raw.key, type: raw.type, country_code: raw.country_code }

  const data = {
    name: raw.name,
    canonical_name: raw.canonical_name,
    type: raw.type,
    country_code: raw.country_code,
    platform_results: { [platform]: nativeResult },
  }
  data.key = locationResultKey(data)

  // supports both db and local formats
  const savedRange = location.radius ?? location
  const value = unit === 'kilometers' ? savedRange.kilometer_range : savedRange.mile_range

  return {
    data,
    qualifier,
    // An excluded city is saved without a radius; it still needs one if switched to include.
    radius: { value: value ?? getDefaultRadiusValue(unit), unit },
  }
}

const allowedLocationTypes = [
  'country',
  'country_group',
  'city',
  'place',
  'custom_location',
  'medium_geo_area',
  'geo_market',
  'region',
  'geo_market',
  'subcity',
  'county',
  'geo_market',
  'custom_location',
  'neighborhood',
  'electoral_district'
]

function locationsFromPlatformTargeting(platformTargeting, platform) {
  let { range_locations: { included, excluded } = {} } = platformTargeting ?? {}
  included = included || []
  excluded = excluded || []
  const mapped = [
    ...included.map((x) => dbLocationToLocation(x, 'include', platform)),
    ...excluded.map((x) => dbLocationToLocation(x, 'exclude', platform)),
  ]
  return mapped.filter((location) => allowedLocationTypes.includes(location.data.type))
}

// Merges targeting[].fb.range_locations and targeting[].google.range_locations
// into ONE list — Figma's "City, region or country" modal shows a single
// shared location list per TC with a per-entry Google/Meta tag, not two
// separate lists. A location present under both platforms (same key) becomes
// one row with both platform tags true; present under only one becomes one
// row with only that tag true. Divergent qualifier/radius between platforms
// for the same key (shouldn't normally happen since the UI always writes
// both together) is resolved by preferring the fb entry.
export const initialState = (tcTargeting = {}) => {
  const fbLocations = locationsFromPlatformTargeting(tcTargeting?.fb, 'fb')
  const googleLocations = locationsFromPlatformTargeting(tcTargeting?.google, 'google')

  const byKey = new Map()
  for (const loc of fbLocations) {
    byKey.set(loc.data.key, { ...loc, platforms: { fb: true, google: false } })
  }
  for (const loc of googleLocations) {
    const existing = byKey.get(loc.data.key)
    if (existing) {
      byKey.set(loc.data.key, {
        ...existing,
        platforms: { ...existing.platforms, google: true },
        data: {
          ...existing.data,
          platform_results: { ...existing.data.platform_results, ...loc.data.platform_results },
        },
      })
    } else {
      byKey.set(loc.data.key, { ...loc, platforms: { fb: false, google: true } })
    }
  }

  return {
    ..._initialState,
    locations: Array.from(byKey.values()),
  }
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actions.addLocation: {
      // filter relevant locations by qualifier
      const { qualifier } = action.payload
      const { included, excluded } = state.locations.reduce((acc, location) => {
        if (location.qualifier === 'include') {
          acc.included.push(location)
        } else if (location.qualifier === 'exclude') {
          acc.excluded.push(location)
        }
        return acc
      }, { included: [], excluded: [] })

      // filter relevant locations by qualifier
      const relevantLocations = qualifier === 'include' ? included : excluded
      let otherLocations = qualifier === 'include' ? excluded : included
      const newLocations = addLocation(relevantLocations, action.payload)

      // Remove excluded supersets if adding an included subset
      if (qualifier === 'include') {
        otherLocations = otherLocations.filter(
          (loc) => !locationContains(loc.data, action.payload.data)
        )
      }

      return { ...state, locations: [...newLocations, ...otherLocations] }
    }
    case actions.removeLocation: {
      const { locationKey } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      const locations = immutableSplice(state.locations, idx, 1)
      return { ...state, locations }
    }
    case actions.clearAll:
      return { ...state, locations: [] }
    case actions.setLocationRadius: {
      const {
        locationKey,
        radius: { value, unit },
        minRadius,
      } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      const location = state.locations[idx]
      const locations = immutableSplice(state.locations, idx, 1, {
        ...location,
        radius: { value: clampRadius(value, unit, minRadius), unit },
        loading: false,
      })
      return { ...state, locations }
    }
    case actions.setQualifier: {
      const { locationKey, qualifier } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      const location = state.locations[idx]

      // readd location to make sure deduplication works correctly
      const { included, excluded } = state.locations.reduce((acc, location) => {
        if (location.qualifier === 'include') {
          acc.included.push(location)
        } else if (location.qualifier === 'exclude') {
          acc.excluded.push(location)
        }
        return acc
      }, { included: [], excluded: [] })

      // filter relevant locations by qualifier
      const relevantLocations = qualifier === 'include' ? included : excluded
      const newLocations = addLocation(relevantLocations, { ...location, qualifier })

      let otherLocations = qualifier === 'include' ? excluded : included
      // filter out the location that was just updated
      otherLocations = otherLocations.filter((loc) => loc.data.key !== locationKey)

      // Remove excluded supersets if changing to included
      if (qualifier === 'include') {
        otherLocations = otherLocations.filter(
          (loc) => !locationContains(loc.data, location.data)
        )
      }

      return { ...state, locations: [...newLocations, ...otherLocations] }
    }
    case actions.togglePlatform: {
      const { locationKey, platform } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === locationKey)
      if (idx === -1) return state
      const location = state.locations[idx]
      const locations = immutableSplice(state.locations, idx, 1, {
        ...location,
        platforms: { ...location.platforms, [platform]: !location.platforms?.[platform] },
      })
      return { ...state, locations }
    }
    case actions.reset: {
      const { tcTargeting, locations } = action.payload
      const newState = initialState(tcTargeting)
      if (locations) {
        newState.locations = locations
      }
      return newState
    }
    case actions.fetchSuggestedRadiusError: {
      const { locationKey: errorLocationKey, error } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === errorLocationKey)
      const location = state.locations[idx]

      const locations = immutableSplice(state.locations, idx, 1, {
        ...location,
        loading: false,
        error,
      })

      return { ...state, locations }
    }
    case actions.fetchSuggestedRadiusLoading: {
      const { locationKey: loadinglocationKey } = action.payload
      const idx = state.locations.findIndex((location) => location.data.key === loadinglocationKey)
      const location = state.locations[idx]
      const locations = immutableSplice(state.locations, idx, 1, { ...location, loading: true })
      return { ...state, locations }
    }
    default:
      return state
  }
}

export const asyncActionHandlers = {
  [actions.addLocationAsync]:
    ({ dispatch }) =>
      async (action) => {
        const { location: data, qualifier, platforms } = action.payload
        const unit = getPreferredRangeUnit()
        const payload = {
          data,
          qualifier,
          platforms,
          loading: data.type === 'city' || data.type === 'place', // fetch suggested radius for cities and addresses
          radius: (data.type === 'city' || data.type === 'place') ? { value: getDefaultRadiusValue(unit), unit } : null,
        }

        dispatch({ type: actions.addLocation, payload })

        if (data.type === 'city' || data.type === 'place') {
          const loc = `${data.country_name ?? data.canonical_name ?? data.name}`
          const response = await targetingApi.getSuggestedRadiusAsync(loc, unit)
          if (response.status !== 200) {
            dispatch({
              type: actions.fetchSuggestedRadiusError,
              payload: { locationKey: data.key, error: 'Error fetching radius' },
            })
            return
          }

          const radius = Math.max(
            getDefaultRadiusValue(unit),
            response.data.suggest_radius || getDefaultRadiusValue(unit),
          )
          dispatch({
            type: actions.setLocationRadius,
            payload: { locationKey: data.key, radius: { value: radius, unit } },
          })
        }
      },
}

export function getPreferredRangeUnit() {
  return i18n.language === 'en' ? 'miles' : 'kilometers'
}

export function clampRadius(value, unit, minRadius) {
  if (unit === 'miles') {
    return Math.min(Math.max(value, minRadius ?? MIN_RADIUS_MILES), MAX_RADIUS_MILES)
  } else {
    return Math.min(Math.max(value, minRadius ?? MIN_RADIUS_KILOMETERS), MAX_RADIUS_KILOMETERS)
  }
}

export function getDefaultRadiusValue(unit) {
  return unit === 'miles' ? MIN_RADIUS_MILES : MIN_RADIUS_KILOMETERS
}

// Meta no longer accepts a radius around an excluded city.
export function hasRadius(location) {
  const { type } = location.data
  return type === 'place' || (type === 'city' && location.qualifier !== 'exclude')
}

export function getRadiusVariations(radius) {
  if (!radius) {
    return {}
  }

  const { value, unit } = radius
  return {
    mile_range: unit === 'miles' ? value : kilometersToMiles(value),
    kilometer_range: unit === 'kilometers' ? value : milesToKilometers(value),
  }
}

// Splits the unified location list back into per-platform range_locations —
// only locations tagged for `platform` and that actually have an identifier
// for it (platform_results.<platform>) are included, since a location found
// on only one platform can't be sent to the other.
export function splitLocationsByPlatform(locations, platform) {
  const included = []
  const excluded = []
  for (const loc of locations) {
    if (!loc.platforms?.[platform]) continue
    const platformResult = loc.data.platform_results?.[platform]
    if (!platformResult) continue

    // Native per-platform id fields only (fb's real `key`, google's real
    // `geo_target_constant_id`) — no merge key on the wire. `canonical_name`
    // rides along so dbLocationToLocation/locationResultKey can recompute
    // the same platform-agnostic merge key on reload (see its comment above).
    const location =
      platform === 'google'
        ? {
          geo_target_constant_id: platformResult.geo_target_constant_id,
          resource_name: platformResult.resource_name,
          target_type: platformResult.target_type,
          name: loc.data.name,
          canonical_name: loc.data.canonical_name,
          type: loc.data.type,
          country_code: loc.data.country_code,
        }
        : {
          key: platformResult.key,
          name: loc.data.name,
          canonical_name: loc.data.canonical_name,
          type: loc.data.type,
          country_code: loc.data.country_code,
        }

    let locationWrapper = { location }
    if (hasRadius(loc)) {
      locationWrapper = { ...locationWrapper, ...getRadiusVariations(loc.radius) }
    }
    (loc.qualifier === 'exclude' ? excluded : included).push(locationWrapper)
  }
  return { included, excluded }
}

function isRemoteTargetingRangeLocationsValid(tcTargeting) {
  const fbValid = isPlatformRangeLocationsValid(tcTargeting?.fb)
  const googleValid = isPlatformRangeLocationsValid(tcTargeting?.google)
  return fbValid || googleValid
}

function isPlatformRangeLocationsValid(platformTargeting) {
  let { range_locations: { included, excluded } = {} } = platformTargeting ?? {}
  included = included || []
  excluded = excluded || []
  return included.length > 0 || excluded.length > 0
}

function isRangeLocationTargetingValid(locations) {
  return locations.length > 0
}

export function shouldOverrideLocalState(tcTargeting, locations) {
  return (
    isRemoteTargetingRangeLocationsValid(tcTargeting) && !isRangeLocationTargetingValid(locations)
  )
}
